import type { DataFilterOperator, DataSourceFilter, ForgeAnalyticsField, ForgeQueryFilter } from "@/frontend/features/Fde/data/types";

const NUMBER_TYPE = /(?:^|\W)(?:u?int\d*|float\d*|decimal|numeric|number)(?:\W|$)/i;
const DATE_TYPE = /(?:date|datetime|timestamp)/i;

export function filterKind(field?: ForgeAnalyticsField) {
  const type = field?.type ?? "";
  if (NUMBER_TYPE.test(type)) return "number";
  if (DATE_TYPE.test(type)) return "date";
  return "text";
}

export function defaultOperator(field?: ForgeAnalyticsField): DataFilterOperator {
  const kind = filterKind(field);
  if (kind === "number") return "range";
  if (kind === "date") return "timeRange";
  return "contains";
}

export function operatorsFor(field?: ForgeAnalyticsField): DataFilterOperator[] {
  const kind = filterKind(field);
  if (kind === "number") return ["equals", "notEquals", "greaterThan", "greaterThanOrEqual", "lessThan", "lessThanOrEqual", "range"];
  if (kind === "date") return ["timeRange"];
  return ["contains", "equals", "notEquals"];
}

export function compileFilters(filters: DataSourceFilter[], fields: ForgeAnalyticsField[]): ForgeQueryFilter[] {
  const byName = new Map(fields.map((field) => [field.name, field]));
  const compiled: ForgeQueryFilter[] = [];
  filters.forEach((filter) => {
    const field = byName.get(filter.field);
    if (!field || !filter.value.trim()) return;
    if (filter.operator === "contains") {
      compiled.push({ field: filter.field, contains: filter.value.trim() });
      return;
    }
    if (filter.operator === "equals" || filter.operator === "notEquals") {
      const raw = filterKind(field) === "number" ? Number(filter.value) : filter.value.trim();
      if (!Number.isNaN(raw)) compiled.push({ field: filter.field, [filter.operator === "equals" ? "oneOf" : "notIn"]: [raw] });
      return;
    }
    if (["greaterThan", "greaterThanOrEqual", "lessThan", "lessThanOrEqual"].includes(filter.operator)) {
      const raw = Number(filter.value);
      if (!Number.isNaN(raw)) compiled.push({ field: filter.field, [filter.operator]: raw });
      return;
    }
    if (!filter.endValue.trim()) return;
    if (filter.operator === "range") {
      const range: [number, number] = [Number(filter.value), Number(filter.endValue)];
      if (!range.some(Number.isNaN)) compiled.push({ field: filter.field, range });
      return;
    }
    const start = Date.parse(`${filter.value}T00:00:00Z`);
    const finish = Date.parse(`${filter.endValue}T23:59:59.999Z`);
    if (!Number.isNaN(start) && !Number.isNaN(finish)) compiled.push({ field: filter.field, timeRange: [start, finish] });
  });
  return compiled;
}
